<template>
    <div class="login-container">
        <!-- Background Elements -->
        <div class="background-elements">
            <div class="bg-circle circle-1"></div>
            <div class="bg-circle circle-2"></div>
            <div class="bg-circle circle-3"></div>
        </div>

        <!-- Main Content -->
        <div class="container-fluid h-100">
            <div class="row h-100 align-items-center justify-content-center">
                <!-- Centered Login Form -->
                <div class="col-lg-4 col-md-4 col-11">
                    <div class="login-section">
                        <div class="login-card">
                            <div class="login-header">
                                <div class="brand-logo-center mb-4">
                                    <div class="logo-icon">
                                        <i class="bi bi-shield-fill-check"></i>
                                    </div>
                                </div>

                                <h3 class="login-title">Welcome Back</h3>
                                <p class="login-subtitle">Sign in to access your professional dashboard</p>
                            </div>
                            <form class="login-form" @submit.prevent="handleLogin">
                                <!-- Email Field -->
                                <div class="form-group">
                                    <label for="email" class="form-label">Email Address</label>
                                    <div class="input-group">
                                        <span class="input-group-text">
                                            <i class="bi bi-envelope"></i>
                                        </span>
                                        <input type="email" id="email" class="form-control" :class="{ 'is-invalid': form.errors.email }" v-model="form.email" placeholder="Enter your email" required>
                                    </div>
                                    <div v-if="form.errors.email" class="invalid-feedback d-block">
                                        {{ form.errors.email }}
                                    </div>
                                </div>

                                <!-- Password Field -->
                                <div class="form-group">
                                    <label for="password" class="form-label">Password</label>
                                    <div class="input-group">
                                        <span class="input-group-text">
                                            <i class="bi bi-lock"></i>
                                        </span>
                                        <input :type="showPassword ? 'text' : 'password'" id="password" class="form-control" :class="{ 'is-invalid': form.errors.password }" v-model="form.password" placeholder="Enter your password" required>
                                        <button type="button" class="input-group-text password-toggle" @click="togglePassword">
                                            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                        </button>
                                    </div>
                                    <div v-if="form.errors.password" class="invalid-feedback d-block">
                                        {{ form.errors.password }}
                                    </div>
                                </div>

                                <!-- Remember & Forgot Password -->
                                <div class="form-options">
                                    <div class="form-check">
                                        <input class="form-check-input" type="checkbox" id="remember" v-model="form.remember">
                                        <label class="form-check-label" for="remember">
                                            Remember me
                                        </label>
                                    </div>
                                    <a href="/forgot-password" class="forgot-password">Forgot password?</a>
                                </div>

                                <!-- Login Button -->
                                <button type="submit" class="btn btn-login" :disabled="form.processing">
                                    <span v-if="form.processing">
                                        <i class="bi bi-arrow-repeat spinning me-2"></i>
                                        Signing in...
                                    </span>
                                    <span v-else>
                                        <i class="bi bi-box-arrow-in-right me-2"></i>
                                        Sign In
                                    </span>
                                </button>


                                <!-- Sign Up Link -->
                                <div class="signup-link">
                                    <p>Don't have an account? <a href="/signup">Create Account</a></p>
                                </div>
                            </form>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { useNotifications } from '@/composables/useNotifications';

// Initialize notifications
const { success, error } = useNotifications();

// Form data using Inertia's useForm for better error handling
const form = useForm({
    email: '',
    password: '',
    remember: false
});

// UI state
const showPassword = ref(false);

// Toggle password visibility
const togglePassword = () => {
    showPassword.value = !showPassword.value;
};

// Handle login form submission
const handleLogin = () => {
    form.post('/login', {
        onSuccess: () => {
            // success('Welcome back! You have been logged in successfully.');
        },
        onError: () => {
            error('Login failed. Please check your credentials and try again.');
        },
        onFinish: () => {
            // Form is automatically reset by Inertia on successful submission
            // Loading state is handled automatically by useForm
        },
    });
};
</script>

<style scoped>
/* Login Container */
.login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    position: relative;
    overflow: hidden;
    font-family: 'Fira Sans', sans-serif;
}

/* Background Elements */
.background-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
}

.bg-circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
}

.circle-1 {
    width: 300px;
    height: 300px;
    top: -150px;
    left: -150px;
    animation: float 6s ease-in-out infinite;
}

.circle-2 {
    width: 200px;
    height: 200px;
    top: 20%;
    right: -100px;
    animation: float 8s ease-in-out infinite reverse;
}

.circle-3 {
    width: 150px;
    height: 150px;
    bottom: -75px;
    left: 30%;
    animation: float 10s ease-in-out infinite;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    50% {
        transform: translateY(-20px) rotate(180deg);
    }
}

/* Login Section */
.login-section {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    position: relative;
    z-index: 2;
}

.login-card {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    padding: 3rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    width: 100%;
    max-width: 480px;
}

/* Centered Brand */
.brand-logo-center {
    display: flex;
    justify-content: center;
}

.logo-icon {
    width: 80px;
    height: 80px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    color: white;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.brand-title-center {
    color: #2c3e50;
    font-weight: 700;
    font-size: 2.2rem;
    text-align: center;
    margin-bottom: 0.5rem;
}

.brand-subtitle-center {
    color: #7f8c8d;
    font-size: 0.9rem;
    text-align: center;
    margin-bottom: 2rem;
    line-height: 1.4;
    font-weight: 500;
}

/* Login Header */
.login-header {
    text-align: center;
    margin-bottom: 2.5rem;
}

.login-title {
    color: #2c3e50;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.login-subtitle {
    color: #7f8c8d;
    font-size: 1rem;
    margin-bottom: 0;
}

/* Form Styling */
.login-form {
    display: grid;
    gap: 1.5rem;
}

.form-group {
    margin-bottom: 0;
}

.form-label {
    color: #2c3e50;
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.input-group {
    position: relative;
}

.input-group-text {
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-right: none;
    color: #1e9579;
    font-size: 1.1rem;
    border-radius: 12px 0 0 12px;
}

.form-control {
    border: 2px solid #e9ecef;
    border-radius: 0 12px 12px 0;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.input-group .form-control {
    border-left: none;
}

.form-control:focus {
    border-color: #1e9579;
    box-shadow: 0 0 0 0.2rem rgba(30, 149, 121, 0.15);
}

.form-control:focus+.input-group-text,
.input-group:focus-within .input-group-text {
    border-color: #1e9579;
    background: rgba(30, 149, 121, 0.1);
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.form-control.is-invalid:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15);
}

.input-group:has(.form-control.is-invalid) .input-group-text {
    border-color: #dc3545;
    background: rgba(220, 53, 69, 0.1);
}

.invalid-feedback {
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.5rem;
    font-weight: 500;
}

.password-toggle {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 0 12px 12px 0 !important;
}

.password-toggle:hover {
    background: rgba(30, 149, 121, 0.1);
    color: #1e9579;
}

/* Form Options */
.form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0;
}

.form-check {
    display: flex;
    align-items: center;
}

.form-check-input {
    margin-right: 0.5rem;
}

.form-check-input:checked {
    background-color: #1e9579;
    border-color: #1e9579;
}

.form-check-label {
    color: #7f8c8d;
    font-size: 0.9rem;
    font-weight: 500;
}

.forgot-password {
    color: #1e9579;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.3s ease;
}

.forgot-password:hover {
    color: #764ba2;
    text-decoration: underline;
}

/* Buttons */
.btn-login {
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    border: none;
    color: white;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
}

.btn-login:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(30, 149, 121, 0.3);
}

.btn-login:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Divider */
.divider {
    position: relative;
    text-align: center;
    margin: 2rem 0 1.5rem;
}

.divider::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #e9ecef;
}

.divider span {
    background: rgba(255, 255, 255, 0.95);
    color: #7f8c8d;
    padding: 0 1rem;
    font-size: 0.9rem;
    font-weight: 500;
}

/* Social Login */
.social-login {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
}

.btn-social {
    padding: 0.75rem 1rem;
    border-radius: 12px;
    font-weight: 500;
    transition: all 0.3s ease;
    text-align: center;
    border: 2px solid transparent;
}

.btn-microsoft {
    background: #00a4ef;
    color: white;
}

.btn-microsoft:hover {
    background: #0078d4;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 164, 239, 0.3);
}

.btn-google {
    background: white;
    color: #757575;
    border-color: #dadce0;
}

.btn-google:hover {
    background: #f8f9fa;
    border-color: #1e9579;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

/* Sign Up Link */
.signup-link {
    text-align: center;
    margin: 0;
}

.signup-link p {
    color: #7f8c8d;
    font-size: 0.9rem;
    margin: 0;
}

.signup-link a {
    color: #1e9579;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
}

.signup-link a:hover {
    color: #764ba2;
    text-decoration: underline;
}



/* Responsive Design */
@media (max-width: 991px) {
    .login-container {
        background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    }

    .login-card {
        margin: 1rem;
        padding: 2rem;
    }

    .login-title {
        font-size: 1.8rem;
    }

    .social-login {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 576px) {
    .login-section {
        padding: 1rem;
    }

    .login-card {
        padding: 1.5rem;
        border-radius: 20px;
    }

    .login-title {
        font-size: 1.6rem;
    }

    .form-options {
        flex-direction: column;
        gap: 1rem;
        align-items: flex-start;
    }
}

/* Focus States for Accessibility */
.form-control:focus,
.btn:focus,
.form-check-input:focus {
    outline: none;
}

.btn:focus-visible {
    box-shadow: 0 0 0 0.2rem rgba(30, 149, 121, 0.5);
}

/* Animation for smooth transitions */
* {
    transition: all 0.3s ease;
}
</style>
